import { useEffect } from "react";
import { Link, useLocation } from "wouter";
import { motion } from "framer-motion";
import { ArrowRight, ChevronDown, ShieldCheck, Cpu, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { GoldParticles } from "@/components/ui/GoldParticles";
import { AnimatedCounter } from "@/components/ui/AnimatedCounter";
import { useAdaptiveCta } from "@/hooks/useAdaptiveCta";
import { useGeoAdaptive } from "@/hooks/useGeoAdaptive";
import { getGeoPack, isActivePack } from "@/lib/geoPacks";
import {
  trackCtaVariantShown,
  trackCtaVariantClick,
  trackCtaVariantConversionStart,
} from "@/lib/uxTracking";

interface HeroProps {
  t: (key: string) => string;
  dir: "ltr" | "rtl";
}

function scrollToServices() {
  const el = document.querySelector("[data-testid='section-services']");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

const BADGE_ICONS = [ShieldCheck, Cpu, Users];

export function Hero({ t, dir }: HeroProps) {
  const isRtl = dir === "rtl";
  const [location] = useLocation();
  const pathname = location?.split("?")[0] ?? (typeof window !== "undefined" ? window.location.pathname : "/");
  const adaptive = useAdaptiveCta(t("home.startEvaluation"));
  const geo = useGeoAdaptive();

  useEffect(() => {
    if (adaptive.isAdaptive && adaptive.variant) {
      trackCtaVariantShown(pathname, adaptive.variant);
    }
  }, [pathname, adaptive.isAdaptive, adaptive.variant]);

  const badges = [
    { key: "home.heroBadge1", Icon: BADGE_ICONS[0] },
    { key: "home.heroBadge2", Icon: BADGE_ICONS[1] },
    { key: "home.heroBadge3", Icon: BADGE_ICONS[2] },
  ];

  // ── Geo copy pack ────────────────────────────────────────────────────────────
  const pack = getGeoPack(geo.country);
  const packActive = geo.isActive && isActivePack(pack);
  const heroTitle = packActive && pack.heroHeadline ? pack.heroHeadline : t("home.heroTitle");
  const heroSubtitle = packActive && pack.heroSubheadline ? pack.heroSubheadline : t("home.heroSubtitle");

  return (
    <section
      className="relative py-28 md:py-44 overflow-hidden"
      data-testid="section-hero"
    >
      {/* Premium background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-background/95" />
        <div className="absolute top-0 left-0 right-0 h-px bg-border/20" />
        <div className="absolute top-1/3 left-1/4 w-[600px] h-[600px] rounded-full bg-primary/[0.035] blur-[140px]" />
        <div className="absolute bottom-1/4 right-1/5 w-[400px] h-[400px] rounded-full bg-primary/[0.025] blur-[120px]" />
        <GoldParticles />
      </div>

      <div className="relative max-w-4xl mx-auto px-6 text-center">

        {/*
          Geo country banner — height always reserved (h-7) to prevent layout shift.
          Content shown only when geo pack is active.
          Variant A: slim text line above premium label, no background block.
        */}
        <div className="h-7 flex items-center justify-center mb-1">
          {packActive && pack.bannerLine && (
            <motion.p
              className="text-xs text-muted-foreground/55 tracking-wide"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.35 }}
              data-testid="geo-country-banner"
              data-geo-country={geo.country}
            >
              {pack.bannerLine}
            </motion.p>
          )}
        </div>

        {/* Premium label — grand format, texte marron */}
        <motion.div
          className="inline-flex items-center gap-3 mb-8 px-6 py-3 rounded-full border-2 border-amber-800/40 text-base md:text-lg font-bold tracking-wider uppercase min-w-0 bg-amber-50/90 dark:bg-amber-950/30 text-amber-900 dark:text-amber-200 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.5)] dark:shadow-none"
          style={{
            background: "linear-gradient(105deg, hsl(38 92% 96%), hsl(38 92% 92%), hsl(38 92% 96%))",
            backgroundSize: "200% auto",
            animation: "shimmer 3s linear infinite",
          }}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <ShieldCheck className="w-5 h-5 relative z-10 shrink-0 text-amber-800 dark:text-amber-300" />
          <span className="relative z-10 whitespace-nowrap">Aurelian Private Care</span>
        </motion.div>

        <motion.h1
          className="text-4xl md:text-6xl lg:text-[5rem] font-serif font-bold tracking-tight text-foreground mb-6 leading-[1.1]"
          style={{ fontFamily: "'Playfair Display', serif" }}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          data-testid="text-hero-title"
        >
          {heroTitle}
        </motion.h1>

        <motion.p
          className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-3 leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          data-testid="text-hero-subtitle"
        >
          {heroSubtitle}
        </motion.p>

        <motion.p
          className="text-sm text-muted-foreground/60 max-w-xl mx-auto mb-10 leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          data-testid="text-hero-description"
        >
          {t("home.heroDescription")}
        </motion.p>

        {/* CTAs */}
        <motion.div
          className={`flex flex-col sm:flex-row items-center justify-center gap-4 mb-10 ${isRtl ? "flex-row-reverse" : ""}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.35 }}
        >
          <Link href="/evaluation">
            <Button
              size="lg"
              className="min-w-[260px] h-12 text-base shadow-md"
              data-testid="button-start-evaluation"
              data-ux-cta-variant={adaptive.variant ?? "default"}
              onClick={() => {
                if (adaptive.variant) {
                  trackCtaVariantClick(adaptive.variant);
                  trackCtaVariantConversionStart(adaptive.variant);
                }
              }}
            >
              {adaptive.primaryLabel}
              <ArrowRight className={`w-4 h-4 ${isRtl ? "mr-2 rotate-180" : "ml-2"}`} />
            </Button>
          </Link>
          <Button
            variant="outline"
            size="lg"
            className="min-w-[220px] h-12 text-base border-border/50"
            data-testid="button-enter-code"
            onClick={scrollToServices}
          >
            {t("home.heroSecondaryLabel")}
            <ChevronDown className={`w-4 h-4 ${isRtl ? "mr-2" : "ml-2"}`} />
          </Button>
        </motion.div>

        {/* Animated counters */}
        <motion.div
          className={`grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mb-8 ${isRtl ? "flex-row-reverse" : ""}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <AnimatedCounter end={2500} suffix="+" label={t("home.counterPatients")} />
          <AnimatedCounter end={30} suffix="+" label={t("home.counterCountries")} />
          <AnimatedCounter end={98} suffix="%" label={t("home.counterSatisfaction")} />
          <AnimatedCounter end={15} suffix="+" label={t("home.counterYears")} />
        </motion.div>

        {/* Confidentiality note */}
        <motion.p
          className="text-xs text-muted-foreground/40 max-w-md mx-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.65 }}
          data-testid="text-hero-note"
        >
          {t("home.note")}
        </motion.p>
      </div>
    </section>
  );
}
